import React, { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import {
  Container, Typography, Box, Button, Paper, Chip, Stack, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete, TextField
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PersonIcon from '@mui/icons-material/Person'
import PlaceIcon from '@mui/icons-material/Place'

interface Company {
  id: number
  name: string
}

interface Member {
  id: number
  name: string
  stage_name: string | null
}

interface LocationEntry {
  id: number
  slug?: string | null
  name: string
  address: string | null
  category: string | null
}

interface Group {
  id: number
  name: string
  company: Company
  gender: string
  group_type: string
  description: string | null
  members: Member[]
  locations: LocationEntry[]
  member_count: number
  created_at: string
  updated_at: string
}

interface Props {
  group: Group
}

const genderLabels: Record<string, string> = {
  girl: 'Girl',
  boy: 'Boy',
  mixed: 'Mixed',
}

const groupTypeLabels: Record<string, string> = {
  group: 'Group',
  band: 'Band',
}

const genderColors: Record<string, 'error' | 'info' | 'warning'> = {
  girl: 'error',
  boy: 'info',
  mixed: 'warning',
}

const categoryLabels: Record<string, string> = {
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  filming_location: 'Filming Location',
  practice_studio: 'Practice Studio',
  entertainment_venue: 'Entertainment Venue',
  shop: 'Shop',
  landmark: 'Landmark',
  other: 'Other',
}

export default function ShowGroup({ group }: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<LocationEntry[]>([])
  const [searching, setSearching] = useState(false)

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete "${group.name}"?`)) {
      router.delete(`/groups/${group.id}`)
    }
  }

  const searchLocations = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([])
      return
    }
    setSearching(true)
    try {
      const response = await fetch(`/api/v1/locations?q=${encodeURIComponent(query)}`)
      const data = await response.json()
      const existingIds = group.locations.map((l) => l.id)
      setSearchResults(data.locations.filter((l: LocationEntry) => !existingIds.includes(l.id)))
    } catch {
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }

  const addLocation = async (locationId: number) => {
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta?.getAttribute('content') || ''
      await fetch(`/api/v1/groups/${group.id}/add_location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ location_id: locationId }),
      })
      router.reload()
    } catch {
      // silently fail
    }
    setSearchQuery('')
    setSearchResults([])
  }

  const removeLocation = async (locationId: number) => {
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta?.getAttribute('content') || ''
      await fetch(`/api/v1/groups/${group.id}/remove_location/${locationId}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken },
      })
      router.reload()
    } catch {
      // silently fail
    }
  }

  return (
    <>
      <Head title={group.name} />
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Button
          component={Link}
          href="/groups"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Back to Groups
        </Button>

        <Paper sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ mb: 1 }}>{group.name}</Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={group.company.name}
                  size="small"
                  variant="outlined"
                  component={Link}
                  href={`/companies/${group.company.id}`}
                  clickable
                />
                <Chip
                  label={genderLabels[group.gender] || group.gender}
                  size="small"
                  color={genderColors[group.gender] || 'default'}
                  variant="outlined"
                />
                <Chip
                  label={groupTypeLabels[group.group_type] || group.group_type}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                component={Link}
                href={`/groups/${group.id}/edit`}
                variant="outlined"
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </Stack>
          </Box>

          {group.description && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body1" color="text.secondary">
                {group.description}
              </Typography>
            </>
          )}
        </Paper>

        <Paper sx={{ p: 4, mb: 4 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <PersonIcon color="action" />
            <Typography variant="h6">Members ({group.members.length})</Typography>
          </Stack>

          {group.members.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No members yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.members.map((member) => (
                    <TableRow key={member.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {member.stage_name || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper sx={{ p: 4 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <PlaceIcon color="action" />
            <Typography variant="h6">Locations ({group.locations.length})</Typography>
          </Stack>

          <Autocomplete
            freeSolo
            filterOptions={(x) => x}
            options={searchResults}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
            inputValue={searchQuery}
            onInputChange={(_, value) => {
              setSearchQuery(value)
              searchLocations(value)
            }}
            onChange={(_, value) => {
              if (value && typeof value !== 'string') {
                addLocation(value.id)
              }
            }}
            loading={searching}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search and add a location"
                placeholder="Type to search locations..."
                size="small"
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  {option.address && (
                    <Typography variant="caption" color="text.secondary">{option.address}</Typography>
                  )}
                </Box>
              </li>
            )}
            sx={{ mb: 2 }}
          />

          {group.locations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No locations assigned yet. Search above to add one.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', width: 80 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.locations.map((location) => (
                    <TableRow key={location.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component={Link}
                          href={`/locations/${location.slug || location.id}`}
                          sx={{ fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {location.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 260, display: 'block' }}>
                          {location.address || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {location.category && (
                          <Chip
                            label={categoryLabels[location.category] || location.category}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeLocation(location.id)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </>
  )
}
